import * as React from "react"
import type { ToastActionElement, ToastProps } from "@/components/ui/toast"

const TOAST_LIMIT = 5
const TOAST_REMOVE_DELAY = 5000
const TOAST_COOLDOWN = 1000 // 1 second cooldown between identical toasts

type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
  key?: string // Optional unique key to prevent duplicates
}

const actionTypes = {
  ADD_TOAST: "ADD_TOAST",
  UPDATE_TOAST: "UPDATE_TOAST",
  DISMISS_TOAST: "DISMISS_TOAST",
  REMOVE_TOAST: "REMOVE_TOAST",
} as const

let count = 0

function genId() {
  count = (count + 1) % Number.MAX_SAFE_INTEGER
  return count.toString()
}

type ActionType = typeof actionTypes

type Action =
  | {
      type: ActionType["ADD_TOAST"]
      toast: ToasterToast
    }
  | {
      type: ActionType["UPDATE_TOAST"]
      toast: Partial<ToasterToast>
    }
  | {
      type: ActionType["DISMISS_TOAST"]
      toastId?: ToasterToast["id"]
    }
  | {
      type: ActionType["REMOVE_TOAST"]
      toastId?: ToasterToast["id"]
    }

interface State {
  toasts: ToasterToast[]
}

// Track toast signatures for deduplication
const toastSignatures = new Map<string, number>() 
const toastTimeouts = new Map<string, ReturnType<typeof setTimeout>>()

const addToRemoveQueue = (toastId: string) => {
  if (toastTimeouts.has(toastId)) {
    return
  }

  const timeout = setTimeout(() => {
    toastTimeouts.delete(toastId)
    dispatch({
      type: "REMOVE_TOAST",
      toastId: toastId,
    })
  }, TOAST_REMOVE_DELAY)

  toastTimeouts.set(toastId, timeout)
}

// Create a signature from toast content for deduplication
const createToastSignature = (toast: ToasterToast): string => {
  // Use the explicit key if provided
  if (toast.key) return toast.key
  
  // Otherwise generate a signature based on content
  return `${String(toast.title)}-${String(toast.description)}-${toast.variant || 'default'}`
}

// Check if this toast is a duplicate
const isDuplicateToast = (toast: ToasterToast, currentToasts: ToasterToast[]): boolean => {
  const signature = createToastSignature(toast)
  const lastShown = toastSignatures.get(signature)
  
  // Check if there's already a visible toast with the same signature
  const visibleDuplicate = currentToasts.some(t => 
    t.id !== toast.id && createToastSignature(t) === signature
  )
  
  // If this toast was shown recently or is currently visible, it's a duplicate
  if ((lastShown && (Date.now() - lastShown) < TOAST_COOLDOWN) || visibleDuplicate) {
    return true
  }
  
  // Update the last shown timestamp
  toastSignatures.set(signature, Date.now())
  return false
}

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "ADD_TOAST":
      // Check for duplicates before adding
      if (isDuplicateToast(action.toast, state.toasts)) {
        console.log("Preventing duplicate toast:", action.toast.title)
        return state
      }
      
      return {
        ...state,
        toasts: [action.toast, ...state.toasts].slice(0, TOAST_LIMIT),
      }

    case "UPDATE_TOAST":
      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === action.toast.id ? { ...t, ...action.toast } : t
        ),
      }

    case "DISMISS_TOAST": {
      const { toastId } = action

      // ! Side effects ! - This could be extracted into a dismissToast() action,
      // but I'll keep it here for simplicity
      if (toastId) {
        addToRemoveQueue(toastId)
      } else {
        state.toasts.forEach((toast) => {
          addToRemoveQueue(toast.id)
        })
      }

      return {
        ...state,
        toasts: state.toasts.map((t) =>
          t.id === toastId || toastId === undefined
            ? {
                ...t,
                open: false,
              }
            : t
        ),
      }
    }
    case "REMOVE_TOAST":
      if (action.toastId === undefined) {
        return {
          ...state,
          toasts: [],
        }
      }
      return {
        ...state,
        toasts: state.toasts.filter((t) => t.id !== action.toastId),
      }
  }
}

const listeners: Array<(state: State) => void> = []

let memoryState: State = { toasts: [] }

function dispatch(action: Action) {
  memoryState = reducer(memoryState, action)
  listeners.forEach((listener) => {
    listener(memoryState)
  })
}

// Extended toast options with key for deduplication
type Toast = Omit<ToasterToast, "id"> & {
  key?: string
}

function toast({ ...props }: Toast) {
  // Create a complete toast object with ID
  const newToast: ToasterToast = {
    ...props,
    id: genId(),
    open: true,
    duration: props.duration || 5000,
    onOpenChange: (open) => {
      if (!open) dismiss(newToast.id)
      if (props.onOpenChange) props.onOpenChange(open)
    }
  }
  
  // Check for duplicates before showing
  if (isDuplicateToast(newToast, memoryState.toasts)) {
    console.log("Preventing duplicate toast:", newToast.title)
    return {
      id: "-1", // Return dummy ID for duplicate toast
      dismiss: () => {},
      update: () => {},
    }
  }
  
  console.log("Toast being shown:", props.title, props.description)
  
  const update = (props: ToasterToast) =>
    dispatch({
      type: "UPDATE_TOAST",
      toast: { ...props, id: newToast.id },
    })
    
  const dismiss = (id: string) => 
    dispatch({ type: "DISMISS_TOAST", toastId: id })

  dispatch({
    type: "ADD_TOAST",
    toast: newToast,
  })

  return {
    id: newToast.id,
    dismiss: () => dismiss(newToast.id),
    update,
  }
}

// Specialized toast shorthand methods
const success = (options: Omit<Toast, "variant">) => 
  toast({ ...options, variant: "success" })

const error = (options: Omit<Toast, "variant">) => 
  toast({ ...options, variant: "destructive" })

function useToast() {
  const [state, setState] = React.useState<State>(memoryState)

  React.useEffect(() => {
    listeners.push(setState)
    return () => {
      const index = listeners.indexOf(setState)
      if (index > -1) {
        listeners.splice(index, 1)
      }
    }
  }, [state])

  return {
    ...state,
    toast,
    success,
    error,
    dismiss: (toastId?: string) => dispatch({ type: "DISMISS_TOAST", toastId }),
  }
}

export { useToast, toast }
